import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import './Dressings.css'
import { AiOutlinePushpin } from "react-icons/ai";
import { getListCover } from "../../../utils/projectImages";
import SortDropdown from "../../../Components/SortDropdown";
import { useSortedItems } from "../../../Hooks/useSortedItems";
import { usePersistedPagination } from "../../../Hooks/usePersistedPagination";

gsap.registerPlugin(ScrollTrigger);

const ITEMS_PER_PAGE = 8;

const dressingsData = [
  { id: 1, title: "Project 01 — Penthouse, Tunis",        link: 'project1' },
  { id: 2, title: "Project 02 — Villa d'Été, Sfax",       link: 'project2' },
  { id: 3, title: "Project 03 — Villa Duplex, Tunis",     link: 'project3' },
  { id: 4, title: "Project 04 — Villa Privée, Tunis",     link: 'project4' },
  { id: 5, title: "Project 05 — Appartement, Tunis",      link: 'project5' },
  { id: 6, title: "Project 06 — Villa Privée, Tunis",     link: 'project6' },
  { id: 7, title: "Project 07 — Villa Privée, Tunis",     link: 'project7' },
  { id: 8, title: "Project 08 — Villa Privée, Tunis",     link: 'project8' },
  { id: 9, title: "Project 09 — Villa Privée, Tunis",     link: 'project9' },
  { id: 10, title: "Project 10 — Villa d'Été , Mornag",     link: 'project10' },
].map((item) => ({
  ...item,
  // Image lue dans le dossier du projet : <projet>/<projet>-bottom.jpg
  pic: getListCover("dressings", item.link),
}));

export default function Dressings({ navigateWithTransition }) {
  const [currentPage, setCurrentPage, sortOrder, setSortOrder] =
    usePersistedPagination("dressings");
  const sortedData = useSortedItems(dressingsData, sortOrder);
  const contentRef = useRef(null);
  const topRef = useRef(null);

  const totalPages = Math.ceil(sortedData.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const visibleItems = sortedData.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Si la page sauvegardée n'existe plus (moins de projets), on revient à la page 1
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;

    const timeout = setTimeout(() => {
      const ctx = gsap.context(() => {
        const text = container.querySelector(".Cuisine_text");
        const figures = container.querySelectorAll(".Cuisine_figure");

        gsap.fromTo(
          text,
          { opacity: 0, y: 60 },
          {
            opacity: 1,
            y: 0,
            duration: 0.9,
            ease: "power2.out",
            delay: currentPage === 1 ? 0.3 : 0,
            scrollTrigger: {
              trigger: text,
              start: "top 88%",
              once: true,
            },
          }
        );

        figures.forEach((fig, i) => {
          gsap.fromTo(
            fig,
            { opacity: 0, y: 60 },
            {
              opacity: 1,
              y: 0,
              duration: 0.8,
              ease: "power2.out",
              delay: i * 0.12,
              scrollTrigger: {
                trigger: fig,
                start: "top 88%",
                once: true,
              },
            }
          );
        });
      }, container);

      return () => ctx.revert();
    }, 50);

    return () => clearTimeout(timeout);
  }, [currentPage, sortOrder]);

  const handleSortChange = (order) => {
    setSortOrder(order);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="Cuisines Cuisines--sortable">
      <div ref={topRef} />

      <div ref={contentRef} className="Cuisines_content">
        <div className="Cuisine_text">
          <h2>Dressings et solutions de rangement</h2>
          <p>
            Ouvert ou fermé, en U, linéaire, en angle ou sous pente, chaque dressing est
            conçu pour s'intégrer harmonieusement à votre intérieur et exploiter
            intelligemment chaque centimètre. Le sur-mesure permet ainsi un véritable
            gain de place, tout en garantissant une utilisation intuitive et confortable.
            Que ce soit pour une villa ou un appartement, nous imaginons des dressings
            uniques, où design, praticité et personnalisation se rencontrent pour créer un
            espace qui vous ressemble.
          </p>
        </div>

        {visibleItems.map((item, index) => (
          <div className="Cuisine_figure" key={item.id}>
            {index === 0 && (
              <div className="Cuisine_sort">
                <SortDropdown value={sortOrder} onChange={handleSortChange} />
              </div>
            )}
            <a href={`/collections/dressings/${item.link}`}>
              <figure>
                <img src={item.pic} alt={item.title} />
                <figcaption>
                  <AiOutlinePushpin size={16} />
                  <span>{item.title}</span>
                </figcaption>
              </figure>
            </a>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="Cuisines_pagination">
          <button
            className="Pagination_btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label="Page précédente"
          >
            ‹
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={`Pagination_btn ${currentPage === page ? "active" : ""}`}
              onClick={() => handlePageChange(page)}
              aria-label={`Page ${page}`}
            >
              {page}
            </button>
          ))}

          <button
            className="Pagination_btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label="Page suivante"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}